import { calculateCursorAndTooltipOnCanvasElement, handleCanvasClick, handleIntermediateConnectorDrawComplete, isClick } from '.';
import { colors, flowBuilderConstants } from '../../../constants/FlowBuilder';
import {
  BaseFlowConfig, ConditionClickHandler, ConnectorBtnClickHandler, ConnectorDrawHandler, ConnectorSelectHandler, DragCompleteHandler, EgressClickHandler,
  ElementSelectHandler, FlowBuilderState, InitiateStepCardClickHandler, ItemBtnClickHandler, StartBtnClickHandler, StepCardMenuClickHandler
} from '../TypeDefs';
import { StepElement } from '../canvas-element';

/**
 * @method isMouseOnStartBtn
 * @description Check if the mouse is on the start button.
 * @param {number} mouseX - The x-coordinate of the mouse pointer.
 * @param {number} mouseY - The y-coordinate of the mouse pointer.
 * @param {BaseFlowConfig} config - The configuration for the flow.
 * @returns {boolean} True if the mouse is on the start button, false otherwise.
 */
export const isMouseOnStartBtn = (mouseX: number, mouseY: number, config: BaseFlowConfig) => (
  mouseX >= flowBuilderConstants.startBtnPosition.x
  && mouseX <= flowBuilderConstants.startBtnPosition.x + config.startBtnWidth
  && mouseY >= flowBuilderConstants.startBtnPosition.y
  && mouseY <= flowBuilderConstants.startBtnPosition.y + config.startBtnHeight
);

/**
 * @method isMouseOnInitiateStepCard
 * @description Check if the mouse is on the initiate step card.
 * @param {number} mouseX - The x-coordinate of the mouse pointer.
 * @param {number} mouseY - The y-coordinate of the mouse pointer.
 * @param {BaseFlowConfig} config - The configuration for the flow.
 * @returns {boolean} True if the mouse is on the initiate step card, false otherwise.
 */
export const isMouseOnInitiateStepCard = (mouseX: number, mouseY: number, config: BaseFlowConfig) => (
  mouseX >= flowBuilderConstants.initiateStepCardPosition.x
  && mouseX <= flowBuilderConstants.initiateStepCardPosition.x + config.stepCardWidth
  && mouseY >= flowBuilderConstants.initiateStepCardPosition.y
  && mouseY <= flowBuilderConstants.initiateStepCardPosition.y + config.stepCardHeight
);

/**
 * @method handleCanvasMouseDown
 * @description Handles the mouse down event on the canvas.
 * @param {Object} options - The options for handling the mouse down event.
 * @param {FlowBuilderState} options.flowBuilderState - The state of the flow builder.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @param {boolean} options.startBtnVisible - Indicates whether the start button is visible.
 * @param {HTMLCanvasElement} options.canvas - The canvas element.
 * @param {boolean} options.initiateStepCardVisible - Indicates whether the initiate step card is visible.
 */
export const handleCanvasMouseDown = ({
  flowBuilderState,
  mouseX,
  mouseY,
  startBtnVisible,
  canvas,
  initiateStepCardVisible
}: {
  flowBuilderState: FlowBuilderState,
  mouseX: number,
  mouseY: number,
  startBtnVisible: boolean,
  canvas: HTMLCanvasElement,
  initiateStepCardVisible: boolean
}) => {
  flowBuilderState.mouseDownStart = Date.now();
  const { canvasElements } = flowBuilderState;
  if (initiateStepCardVisible && isMouseOnInitiateStepCard(mouseX, mouseY, flowBuilderState.flowConfig)) {
    flowBuilderState.clickingInitiateStepCard = true;
    return;
  }
  if (startBtnVisible && isMouseOnStartBtn(mouseX, mouseY, flowBuilderState.flowConfig)) {
    flowBuilderState.clickingStartBtn = true;
    return;
  }
  let panningCanvas = true;
  for (let i = canvasElements.length - 1; i >= 0; i -= 1) {
    const element = canvasElements[i];
    if (
      element.mouseOnWhichConnectorLine(mouseX, mouseY)
      || element.mouseOnWhichButton(mouseX, mouseY)
      || element.isMouseOnCondition(mouseX, mouseY)
      || element.isMouseOnBtnGroupBody(mouseX, mouseY)
      || element.mouseOnWhichConnectorBtn(mouseX, mouseY)
      || (element instanceof StepElement && element.isMouseOnStepMenuIcon(mouseX, mouseY))
    ) {
      // no op on connector line
      return;
    }
    let orientation = element.isMouseOnIncomingConnector(mouseX, mouseY);
    if (orientation) {
      // if mouse on incoming connector, record for drawing connector
      flowBuilderState.drawingConnectorElement = {
        element,
        isIncoming: true,
        orientation
      };
      panningCanvas = false;
      break;
    }
    orientation = element.isMouseOnOutgoingConnector(mouseX, mouseY);
    if (orientation) {
      // if mouse on outgoing connector, record for drawing connector
      flowBuilderState.drawingConnectorElement = {
        element,
        isIncoming: false,
        orientation
      };
      panningCanvas = false;
      break;
    } else if (element.isMouseOnBody(mouseX, mouseY)) {
      // if mouse on item body, record for dragging
      element.dragStart(mouseX, mouseY);
      flowBuilderState.draggingElement = element;
      panningCanvas = false;
      break;
    }
  }
  if (panningCanvas) {
    flowBuilderState.panningCanvas = panningCanvas;
    flowBuilderState.cursorStyle = 'grabbing';
    canvas.style.cursor = 'grabbing';
  }
};

/**
 * @method handleCanvasMouseMove
 * @description Handles the mouse move event on the canvas.
 * @param {Object} options - The options for handling the mouse move event.
 * @param {FlowBuilderState} options.flowBuilderState - The state of the flow builder.
 * @param {HTMLDivElement} options.tooltipElem - The tooltip element.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @param {CanvasRenderingContext2D} options.ctx - The canvas rendering context.
 * @param {boolean} options.startBtnVisible - Indicates whether the start button is visible.
 * @param {number} options.movementX - The horizontal movement of the mouse.
 * @param {number} options.movementY - The vertical movement of the mouse.
 * @param {number} options.clientX - The x-coordinate of the mouse position relative to the client area.
 * @param {number} options.clientY - The y-coordinate of the mouse position relative to the client area.
 * @param {HTMLCanvasElement} options.canvas - The canvas element.
 * @param {Function} options.drawElements - The function to redraw the canvas elements.
 * @param {boolean} options.initiateStepCardVisible - Indicates whether the initiate step card is visible.
 */
export const handleCanvasMouseMove = ({
  flowBuilderState,
  tooltipElem,
  mouseX,
  mouseY,
  ctx,
  startBtnVisible,
  movementX,
  movementY,
  clientX,
  clientY,
  canvas,
  drawElements,
  initiateStepCardVisible,
  readOnly
}: {
  flowBuilderState: FlowBuilderState,
  tooltipElem: HTMLDivElement,
  mouseX: number,
  mouseY: number,
  ctx: CanvasRenderingContext2D,
  startBtnVisible: boolean,
  movementX: number,
  movementY: number,
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
  drawElements: () => void,
  initiateStepCardVisible: boolean,
  readOnly: boolean,
}) => {
  if (flowBuilderState.mouseDownStart > 0) {
    flowBuilderState.mousemove.x += Math.abs(movementX);
    flowBuilderState.mousemove.y += Math.abs(movementY);
  }
  const { draggingElement, drawingConnectorElement, canvasElements } = flowBuilderState;
  if (draggingElement?.config.draggable) {
    // change position of the dragging item
    draggingElement.dragTo(mouseX, mouseY);
    drawElements();
  }
  if (draggingElement) {
    return;
  }
  if (drawingConnectorElement && !readOnly) {
    canvasElements.forEach((element) => {
      if (element.isMouseOnBody(mouseX, mouseY) && element !== drawingConnectorElement.element) {
        element.config.fillColor = colors.primary;
        return;
      }
      element.config.fillColor = colors.white;
    });
    drawElements();
    // draw dotted line from recorded item
    drawingConnectorElement.element.drawIntermediateConnector(
      ctx,
      drawingConnectorElement.isIncoming,
      mouseX,
      mouseY
    );
    return;
  }
  if (
    (startBtnVisible && isMouseOnStartBtn(mouseX, mouseY, flowBuilderState.flowConfig))
    || (initiateStepCardVisible && isMouseOnInitiateStepCard(mouseX, mouseY, flowBuilderState.flowConfig))
  ) {
    flowBuilderState.cursorStyle = 'pointer';
    canvas.style.cursor = flowBuilderState.cursorStyle;
    return;
  }
  if (flowBuilderState.panningCanvas) {
    flowBuilderState.cursorStyle = 'grabbing';
    flowBuilderState.translateX += movementX;
    flowBuilderState.translateY += movementY;
    drawElements();
    canvas.style.cursor = flowBuilderState.cursorStyle;
    return;
  }
  const { cursor, tooltip } = calculateCursorAndTooltipOnCanvasElement({
    canvasElements,
    mouseX,
    mouseY
  });
  drawElements();

  flowBuilderState.cursorStyle = cursor;
  canvas.style.cursor = flowBuilderState.cursorStyle;

  if (tooltip) {
    tooltipElem.innerText = tooltip;
    tooltipElem.style.opacity = '1';
    tooltipElem.style.transform = `translate(${clientX - 20}px, ${clientY + 15}px)`;
    tooltipElem.style.zIndex = '99';
    return;
  }
  tooltipElem.style.opacity = '0';
  tooltipElem.style.zIndex = '-1';
};

/**
 * @method handleCanvasMouseUp
 * @description Handles the mouse up event on the canvas.
 * @param {Object} options - The options for handling the mouse up event.
 * @param {FlowBuilderState} options.flowBuilderState - The state of the flow builder.
 * @param {number} options.mouseX - The x-coordinate of the mouse position.
 * @param {number} options.mouseY - The y-coordinate of the mouse position.
 * @param {number} options.clientX - The x-coordinate of the mouse position relative to the client area.
 * @param {number} options.clientY - The y-coordinate of the mouse position relative to the client area.
 * @param {HTMLCanvasElement} options.canvas - The canvas element.
 * @param {Function} [options.onStartBtnClick] - The callback function for start button click.
 * @param {Function} [options.onElementSelect] - The callback function for element selection.
 * @param {Function} [options.onDragComplete] - The callback function for drag completion.
 * @param {Function} [options.onEgressClick] - The callback function for egress click.
 * @param {Function} [options.onConnectorDraw] - The callback function for connector draw.
 * @param {Function} [options.onConnectorSelect] - The callback function for connector selection.
 * @param {Function} [options.onItemBtnClick] - The callback function for item button click.
 * @param {Function} [options.onConnectorBtnClick] - The callback function for connector button click.
 * @param {Function} [options.onConditionClick] - The callback function for condition click.
 * @param {Function} options.drawElements - The function to redraw the canvas elements.
 * @param {Function} [options.onInitiateStepCardClick] - The callback function for initiate step card click.
 * @param {Function} [options.onStepCardMenuClick] - The callback function for step card menu click.
 */
export const handleCanvasMouseUp = ({
  flowBuilderState,
  mouseX,
  mouseY,
  clientX,
  clientY,
  canvas,
  onStartBtnClick,
  onElementSelect,
  onDragComplete,
  onEgressClick,
  onConnectorDraw,
  onConnectorSelect,
  onItemBtnClick,
  onConnectorBtnClick,
  onConditionClick,
  drawElements,
  onInitiateStepCardClick,
  onStepCardMenuClick,
}: {
  flowBuilderState: FlowBuilderState,
  mouseX: number,
  mouseY: number,
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
  onStartBtnClick?: StartBtnClickHandler,
  onElementSelect?: ElementSelectHandler,
  onDragComplete?: DragCompleteHandler,
  onEgressClick?: EgressClickHandler,
  onConnectorDraw?: ConnectorDrawHandler,
  onConnectorSelect?: ConnectorSelectHandler,
  onItemBtnClick?: ItemBtnClickHandler,
  onConnectorBtnClick?: ConnectorBtnClickHandler,
  onConditionClick?: ConditionClickHandler,
  drawElements: () => void,
  onInitiateStepCardClick?: InitiateStepCardClickHandler,
  onStepCardMenuClick?: StepCardMenuClickHandler,
}) => {
  const {
    draggingElement,
    drawingConnectorElement,
    canvasElements,
    mouseDownStart,
    mousemove,
    clickingStartBtn,
    clickingInitiateStepCard
  } = flowBuilderState;
  if (clickingInitiateStepCard) {
    onInitiateStepCardClick?.call(null);
  } else if (clickingStartBtn) {
    onStartBtnClick?.call(null);
  } else if (draggingElement) {
    if (!draggingElement.hasDragged() && isClick(mouseDownStart, mousemove)) {
      // click detected, mark everything as deselected
      canvasElements.forEach((e) => e.deselect());
      draggingElement.select();
      onElementSelect?.call(null, draggingElement.elementId);
      drawElements();
    } else if (onDragComplete) {
      onDragComplete(
        draggingElement.elementId,
        draggingElement.left,
        draggingElement.top
      );
    }
  } else if (drawingConnectorElement) {
    canvasElements.every((element) => {
      element.config.fillColor = colors.white;
      return true;
    });
    handleIntermediateConnectorDrawComplete({
      canvasElements,
      drawingConnectorElement,
      mouseDownStart,
      mousemove,
      mouseX,
      mouseY,
      onConnectorDraw,
      onEgressClick
    });
    drawElements();
  } else if (isClick(mouseDownStart, mousemove)) {
    handleCanvasClick({
      canvas,
      flowBuilderState,
      mouseX,
      mouseY,
      clientX,
      clientY,
      onConditionClick,
      onConnectorBtnClick,
      onConnectorSelect,
      onElementSelect,
      onItemBtnClick,
      onStepCardMenuClick,
    });
    drawElements();
  }

  if (flowBuilderState.panningCanvas) {
    flowBuilderState.cursorStyle = flowBuilderConstants.defaultCursor;
    canvas.style.cursor = flowBuilderState.cursorStyle;
  }

  flowBuilderState.draggingElement = undefined;
  flowBuilderState.mouseDownStart = 0;
  flowBuilderState.drawingConnectorElement = undefined;
  flowBuilderState.panningCanvas = false;
  flowBuilderState.clickingStartBtn = false;
  flowBuilderState.clickingInitiateStepCard = false;
  flowBuilderState.mousemove = { x: 0, y: 0 };
};
